import { ProductTier } from '@sparemin/api-sdk';
import * as React from 'react';
import { Form } from 'react-bootstrap';
import { noop } from 'underscore';

import { FileUploadMethod } from 'components/FileUploader/FileUploaderDropzone';
import { MediaImportOption } from 'components/FileUploader/types';
import LanguageSelector from 'components/LanguageSelector';
import MediaUploadModal, {
  YouTubeUploadModal,
} from 'containers/MediaUploadModal';
import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import VideoUploader, { VideoUploaderProps } from 'containers/VideoUploader';
import useOnMount from 'hooks/useOnMount';
import bem from 'utils/bem';

import UploadVideoStepSelectedMessage from './UploadVideoStepSelectedMessage';
import useMediaImport from './useMediaImport';

interface IProps extends VideoUploaderProps {
  onLanguageSelect?: (language: string) => void;
  onMediaAccepted?: (args: MediaImportedArgs) => void;
  language: string;
  videoSelected?: boolean;
  tier: ProductTier;
  defaultFile?: File;
  uploadMethod?: FileUploadMethod;
  /**
   * The default source to use when opening the media upload modal automatically.
   * Defaults to `googleDrive`.
   */
  defaultMediaImportOption?: MediaImportOption;
  /**
   * Whether to open the media upload modal automatically when the step is mounted.
   * If `defaultMediaImportOption` is not set, `googleDrive` will be used as default.
   */
  openMediaUploadModalOnInit?: boolean;
  /**
   * Controls what renders after a video has been selected. Defaults to
   * `<UploadVideoStep.SelectedMessage />`. Callers that want to compose with
   * the default can render `UploadVideoStep.SelectedMessage` themselves.
   */
  selectedMessage?: React.ReactNode;
}

interface UploadVideoStepComponent extends React.FC<IProps> {
  SelectedMessage: typeof UploadVideoStepSelectedMessage;
}

const UploadVideoStep: UploadVideoStepComponent = props => {
  const {
    language,
    onLanguageSelect = noop,
    onMediaAccepted,
    videoSelected = false,
    tier,
    defaultMediaImportOption = 'googleDrive',
    openMediaUploadModalOnInit = false,
    selectedMessage = <UploadVideoStepSelectedMessage />,
    ...uploaderProps
  } = props;

  const { onMediaImport, onYouTubeMediaImport } = useMediaImport({
    onMediaAccepted,
  });

  const isUsingMediaImporter = uploaderProps.uploadMethod === 'mediaImporter';

  const b = bem('upload-video-step');

  useOnMount(async () => {
    if (openMediaUploadModalOnInit) {
      if (defaultMediaImportOption === 'youtube') {
        return onYouTubeMediaImport();
      }

      onMediaImport(defaultMediaImportOption);
    }

    return undefined;
  });

  return (
    <div
      className={b({
        'media-importer': isUsingMediaImporter,
      })}
    >
      <div
        className={b('body', {
          contents: isUsingMediaImporter,
        })}
      >
        <VideoUploader
          {...uploaderProps}
          onClickMediaImportOption={onMediaImport}
        >
          {videoSelected && selectedMessage}
        </VideoUploader>
        <Form className="upload-video-step__language-form" horizontal>
          <LanguageSelector
            className="upload-video-step__language-field"
            horizontal={{
              control: { lg: 7, xs: 12 },
              label: { lg: 5, xs: 12 },
            }}
            labelClassName="upload-video-step__form-label"
            onChange={lang => {
              onLanguageSelect(lang);
            }}
            value={language}
          />
        </Form>
      </div>

      <MediaUploadModal />
      <YouTubeUploadModal />
    </div>
  );
};

UploadVideoStep.SelectedMessage = UploadVideoStepSelectedMessage;

export default UploadVideoStep;

export { IProps as UploadVideoStepProps };
